import sumBy from 'lodash/sumBy';
import { safeSum } from 'Pmi/utils';

import {
	getValueFromSplitByDimention,
	groupItemsByIdForSegmentationListExcluded as groupItemsByIdForSegmentation,
} from 'utils/detailsChartDataConverter';

const BLANK_VALUE = {
	value: 0,
	valueNegative: 0,
	valuePositive: 0,
	tailValue: 0,
	tailValueNegative: 0,
	tailValuePositive: 0,
};

class WheelToByCategoryConverter {
	getSegmentsValues(data, searchParentId, selectedSegmentName) {
		if (selectedSegmentName) {
			const filteredList = data.filter(
				item => item.parentId === searchParentId && !item.isTailVendorsNode
			);

			const grouppedList = groupItemsByIdForSegmentation(
				filteredList,
				'segmentName'
			).filter(item => item.segmentName);

			return grouppedList;
		}

		return null;
	}

	getTotalsData(data, parentId, selectedSegmentName) {
		const getItemValue = (item, key = '') =>
			item ? item[`value${key}`] + item[`tailValue${key}`] : 0;
		const getItemTailValue = (item, key = '') =>
			item ? item[`tailValue${key}`] : 0;

		let dataItem = null;

		if (selectedSegmentName) {
			if (parentId === '/') {
				const list = data.filter(
					item =>
						item.parentId === parentId &&
						item.segmentName === selectedSegmentName
				);

				dataItem = {
					value: sumBy(list, 'value'),
					valuePositive: sumBy(list, 'valuePositive'),
					valueNegative: sumBy(list, 'valueNegative'),
					tailValue: sumBy(list, 'tailValue'),
					tailValuePositive: sumBy(list, 'tailValuePositive'),
					tailValueNegative: sumBy(list, 'tailValueNegative'),
				};
			} else {
				dataItem = data.find(
					item =>
						item.id === parentId && item.segmentName === selectedSegmentName
				) || { ...BLANK_VALUE };
			}
		} else {
			const dataGrouped = groupItemsByIdForSegmentation(
				data.filter(item => item.id === parentId)
			);

			dataItem = dataGrouped[0];
		}

		const value = getItemValue(dataItem);
		const valueNegative = getItemValue(dataItem, 'Negative');
		const valuePositive = getItemValue(dataItem, 'Positive');

		const tailValue = getItemTailValue(dataItem);
		const tailValueNegative = getItemTailValue(dataItem, 'Negative');
		const tailValuePositive = getItemTailValue(dataItem, 'Positive');

		return {
			value,
			valueNegative,
			valuePositive,

			tailValue,
			tailValueNegative,
			tailValuePositive,
		};
	}

	getParentId(filters) {
		const { hierarchy, categoryName } = filters;
		let hierarchyString = '';

		if (hierarchy) {
			hierarchyString = Array.isArray(hierarchy)
				? hierarchy.map(encodeURIComponent).join('/')
				: encodeURIComponent(hierarchy);
		}

		return `${hierarchyString ? '/' + hierarchyString : ''}/${
			categoryName ? encodeURIComponent(categoryName) + '/' : ''
		}`;
	}

	getHierarchy(filters) {
		const { hierarchy, categoryName } = filters;

		if (Array.isArray(hierarchy)) {
			return categoryName ? [...hierarchy, categoryName] : hierarchy;
		} else if (hierarchy) {
			return categoryName ? [hierarchy, categoryName] : hierarchy;
		} else {
			return categoryName ? categoryName : hierarchy;
		}
	}

	getCategorizationList(data, filters) {
		const searchParentId = this.getParentId(filters);
		const {
			isTailSpend,
			selectedSegmentName,
			addCompaniesData,
			showTailSpend,
		} = filters;

		const itemsSorter = (itemA, itemB) =>
			Math.abs(itemB.value) - Math.abs(itemA.value);

		const filteredList = data.filter(
			item => item.parentId === searchParentId && !item.isTailVendorsNode
		);

		const itemMapper = ({ label, ...item }) => ({
			...item,
			name: label,
			value: addCompaniesData
				? getValueFromSplitByDimention(item.splitByDimension)
				: item.value,
		});

		let convertedList = filteredList
			.map(itemMapper)
			.sort(itemsSorter)
			.map(item => {
				const itemId = item.id;

				let subCategoriesL4 = data
					.filter(
						item =>
							item.parentId &&
							item.parentId.indexOf(itemId) === 0 &&
							item.depth === 4 &&
							(isTailSpend ? item.tailValue : item.value) &&
							!item.isTailVendorsNode
					)
					.map(itemMapper)
					.sort(itemsSorter);

				let subCategories = data
					.filter(
						item =>
							item.parentId === itemId &&
							// disabled filtering out blank items for addCompaniesData
							(filters.addCompaniesData ||
								(isTailSpend ? item.tailValue : item.value)) &&
							!item.isTailVendorsNode
					)
					.map(itemMapper)
					.sort(itemsSorter);

				if (isTailSpend) {
					if (selectedSegmentName) {
						subCategoriesL4 = this.filterItemsForSegmentation(
							subCategoriesL4,
							filters
						);
						subCategories = this.filterItemsForSegmentation(
							subCategories,
							filters
						);
					} else {
						subCategoriesL4 = groupItemsByIdForSegmentation(subCategoriesL4);
						subCategories = groupItemsByIdForSegmentation(subCategories);

						if (addCompaniesData) {
							const convertItem = item => {
								const tailItem = this.getTailSpendItemData(data, item.id);

								if (!tailItem) {
									return {
										...item,
										splitByDimension: {},
									};
								}

								return {
									...item,
									splitByDimension: tailItem.splitByDimension,
									value: getValueFromSplitByDimention(
										tailItem.splitByDimension
									),
								};
							};
							subCategories = subCategories.map(convertItem);
						}
					}
				} else if (addCompaniesData && !showTailSpend) {
					const convertItem = item => {
						const tailItem = this.getTailSpendItemData(data, item.id);

						if (!tailItem) {
							return item;
						}

						const keys = [
							...new Set([
								...Object.keys(item.splitByDimension),
								...Object.keys(tailItem.splitByDimension),
							]),
						];

						const splitByDimension = keys.reduce(
							(result, key) => ({
								...result,
								[key]: safeSum(
									item.splitByDimension[key],
									tailItem.splitByDimension[key]
								),
							}),
							{}
						);

						return {
							...item,
							splitByDimension,
						};
					};
					subCategories = subCategories.map(convertItem);
				}

				return {
					...item,
					subCategoriesL4,
					subCategories,
				};
			});

		if (selectedSegmentName) {
			convertedList = this.filterItemsForSegmentation(convertedList, filters);
		} else {
			convertedList = groupItemsByIdForSegmentation(convertedList);
		}

		return convertedList;
	}

	filterItemsForSegmentation = (list, filters) => {
		const { selectedSegmentName } = filters;

		const filteredList = list.filter(
			item => item.segmentName === selectedSegmentName
		);

		return filteredList;
	};

	getDataWithNegatives(data) {
		return data.map(item => {
			const resItem = { ...item };

			if (item.value > 0) {
				resItem.valuePositive = resItem.valuePositive || item.value;
				resItem.valueNegative = Math.abs(resItem.valueNegative) || 0;
			} else if (item.value < 0) {
				resItem.valuePositive = resItem.valuePositive || 0;
				resItem.valueNegative = Math.abs(resItem.valueNegative || item.value);
			} else {
				resItem.valuePositive = 0;
				resItem.valueNegative = 0;
			}

			if (item.tailValue > 0) {
				resItem.tailValuePositive = resItem.tailValuePositive || item.tailValue;
				resItem.tailValueNegative = Math.abs(resItem.tailValueNegative) || 0;
			} else if (item.tailValue < 0) {
				resItem.tailValuePositive = resItem.tailValuePositive || 0;
				resItem.tailValueNegative = Math.abs(
					resItem.tailValueNegative || item.tailValue
				);
			} else {
				resItem.tailValuePositive = 0;
				resItem.tailValueNegative = 0;
			}

			return resItem;
		});
	}

	getTailSpendItemData(data, searchParentId) {
		const tailSpendItem = data
			.filter(item => item.parentId === searchParentId)
			.find(item => item.isTailVendorsNode);

		return tailSpendItem
			? {
					value: tailSpendItem.value,
					valueNegative: tailSpendItem.valueNegative,
					valuePositive: tailSpendItem.valuePositive,
					splitByDimension: tailSpendItem.splitByDimension,
					subCategories: [],
			  }
			: {
					value: 0,
					valueNegative: 0,
					valuePositive: 0,
					subCategories: [],
					splitByDimension: {},
			  };
	}

	getSecondaryCategorization(name, subCategories) {
		return {
			name,
			subCategories,
			...BLANK_VALUE,
		};
	}

	convertData(
		{
			nonAddressableData: originalNonAddressableData,
			addressableData: originalAddressableData,
		},
		filters
	) {
		// TODO: fix this code duplication (getDataWithNegatives method)
		const addressableData = this.getDataWithNegatives(originalAddressableData);
		const nonAddressableData = this.getDataWithNegatives(
			originalNonAddressableData
		);

		const {
			categoryLevel,
			categoryName,
			showNonSourceable,
			selectedSegmentName,
			isTailSpend,
		} = filters;
		const parentId = this.getParentId(filters);
		let convertedSpendCategorizationChartData;

		if (showNonSourceable) {
			const categorizationNonAddressable = this.getCategorizationList(
				nonAddressableData,
				filters
			);

			convertedSpendCategorizationChartData = {
				parentId,
				categoryLevel,
				categoryName,
				total: this.getTotalsData(
					nonAddressableData,
					parentId,
					selectedSegmentName
				),
				segmentsValues: this.getSegmentsValues(
					nonAddressableData,
					parentId,
					selectedSegmentName
				),
				tailSpend: this.getTailSpendItemData(nonAddressableData, parentId),
				categorization: categorizationNonAddressable,
				secondaryCategorization: this.getSecondaryCategorization(
					'Sourceable Spend',
					categorizationNonAddressable
				),
			};
		} else {
			const categorizationAddressable = this.getCategorizationList(
				addressableData,
				filters
			);

			convertedSpendCategorizationChartData = {
				parentId,
				categoryLevel,
				categoryName,
				total: this.getTotalsData(
					addressableData,
					parentId,
					selectedSegmentName
				),
				segmentsValues: this.getSegmentsValues(
					addressableData,
					parentId,
					selectedSegmentName
				),
				tailSpend: this.getTailSpendItemData(addressableData, parentId),
				categorization: categorizationAddressable,
				secondaryCategorization: this.getSecondaryCategorization(
					'Non-Sourceable Spend',
					categorizationAddressable
				),
			};
		}

		if (isTailSpend) {
			convertedSpendCategorizationChartData = wheelToByCategoryConverter.convertToTailSpendData(
				convertedSpendCategorizationChartData,
				filters
			);
		}

		return convertedSpendCategorizationChartData;
	}

	convertToTailSpendData = (
		{ categorization, secondaryCategorization, total, ...rest },
		params
	) => {
		const listSorter = (a, b) =>
			b.value - a.value || b.valuePositive - a.valuePositive;

		const convertItem = item => {
			return {
				...item,
				value: item.tailValue,
				valueNegative: item.tailValueNegative,
				valuePositive: item.tailValuePositive,
				...(item.subCategories
					? {
							subCategories: convertList(item.subCategories),
					  }
					: {}),
				...(item.subCategoriesL4
					? {
							subCategoriesL4: convertList(item.subCategoriesL4),
					  }
					: {}),
			};
		};

		const convertList = list =>
			list
				.filter(item => !item.isTailVendorsNode)
				.map(item => convertItem(item))
				.filter(
					item =>
						params.addCompaniesData ||
						item.value ||
						item.valuePositive ||
						item.valueNegative
				)
				.sort(listSorter);

		const res = {
			...rest,
			total: {
				...total,
				value: total.tailValue,
				valueNegative: total.tailValueNegative,
				valuePositive: total.tailValuePositive,
			},
			categorization: convertList(categorization),
			secondaryCategorization: {
				...secondaryCategorization,
				...convertItem(secondaryCategorization),
			},
		};

		return res;
	};
}

export const wheelToByCategoryConverter = new WheelToByCategoryConverter();
